import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Calendar, User, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-xl font-manrope text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center py-24" data-testid="post-not-found">
        <div className="text-center">
          <h1 className="text-4xl font-unbounded font-black mb-4">Post Not Found</h1>
          <Link to="/news" className="text-accent hover:underline font-manrope">Back to News</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="news-detail-page">
      <section className="py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/news" className="inline-flex items-center gap-2 text-white hover:text-accent transition-colors mb-8">
            <ArrowLeft size={20} /> Back to News
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-unbounded font-black mb-6"
            data-testid="post-title"
          >
            {post.title}
          </motion.h1>
          <div className="flex items-center gap-6 text-gray-300 font-manrope">
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              <span>{new Date(post.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <User size={16} />
              <span>{post.author}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none font-manrope" data-testid="post-content">
            {post.content.split('\n').map((paragraph, index) => (
              <p key={`${slug}-p-${index}`} className="mb-6 text-gray-700">{paragraph}</p>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
